import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReports, useUpdateShare, useDeleteReport, useGeneratePdf } from '../hooks/useReports';
import ScorePill from '../components/ui/ScorePill';
import Badge from '../components/ui/Badge';
import Modal from '../components/ui/Modal';
import ProjectContextBadge from '../components/layout/ProjectContextBadge';

export default function Reports() {
  const navigate = useNavigate();
  const { data, isLoading } = useReports();
  const updateShare = useUpdateShare();
  const deleteReport = useDeleteReport();
  const generatePdf = useGeneratePdf();
  const reports = data?.data || [];

  const [shareModal, setShareModal] = useState(null);
  const [password, setPassword] = useState('');
  const [copied, setCopied] = useState(false);

  const handleShare = async (report, enabled) => {
    await updateShare.mutateAsync({ id: report.id, is_shared: enabled });
    if (enabled) setShareModal(report);
  };

  const handleSetPassword = async () => {
    if (!password || !shareModal) return;
    await updateShare.mutateAsync({ id: shareModal.id, password });
    setPassword('');
    setShareModal(null);
  };

  const handlePdf = (report) => {
    window.open(`/api/pdf/${report.id}`, '_blank');
  };

  const shareUrl = (report) => `${window.location.origin}/api/share/${report.share_token}`;

  return (
    <div>
      <div className="flex items-start justify-between mb-5">
        <div>
          <ProjectContextBadge />
          <h1 className="text-[23px] font-bold mb-0.5">Reports & PDF</h1>
          <p className="text-gray-500 text-[13.5px]">Berichte aus Analysen erstellen, als PDF exportieren und teilen</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-black/[.07]">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 text-left">
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Kunde / URL</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Seitentyp</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Datum</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Score</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Sharing</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium text-right">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((r) => {
              const a = r.analysis || {};
              return (
                <tr key={r.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/50 transition-colors">
                  <td className="px-5 py-3.5">
                    <div className="text-[13px] font-medium truncate max-w-[250px]">{a.contact?.company || a.contact?.name || r.client_name || '—'}</div>
                    <div className="text-[11px] text-gray-500 truncate max-w-[250px]">{a.url || '—'}</div>
                  </td>
                  <td className="px-5 py-3.5"><Badge bg="#E6F4F2" color="#0C9488">{a.page_type || '—'}</Badge></td>
                  <td className="px-5 py-3.5 text-[12.5px] text-gray-500">{new Date(r.created_at).toLocaleDateString('de')}</td>
                  <td className="px-5 py-3.5">{a.score ? <ScorePill score={a.score} /> : '—'}</td>
                  <td className="px-5 py-3.5">
                    {r.is_shared ? (
                      <button onClick={() => setShareModal(r)} className="flex items-center gap-1.5">
                        <Badge bg="#D1FAE5" color="#10B981">Geteilt</Badge>
                      </button>
                    ) : (
                      <Badge bg="#F3F4F6" color="#6B7280">Privat</Badge>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => navigate(`/cro/analyses/${a.id}`)}
                        className="text-[12px] text-teal font-medium hover:underline"
                      >
                        Ansehen
                      </button>
                      <button
                        onClick={() => handlePdf(r)}
                        disabled={generatePdf.isPending}
                        className="text-[12px] text-blue-600 font-medium hover:underline disabled:opacity-50"
                      >
                        PDF
                      </button>
                      <button
                        onClick={() => handleShare(r, !r.is_shared)}
                        className="text-[12px] text-purple-600 font-medium hover:underline"
                      >
                        {r.is_shared ? 'Link' : 'Teilen'}
                      </button>
                      <button
                        onClick={() => { if (confirm('Report löschen?')) deleteReport.mutate(r.id); }}
                        className="text-[12px] text-red-500 font-medium hover:underline"
                      >
                        Löschen
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {reports.length === 0 && !isLoading && (
          <div className="py-12 text-center text-gray-400 text-sm">
            Noch keine Reports. Erstelle einen Report aus einer abgeschlossenen Analyse.
          </div>
        )}
        {isLoading && <div className="py-12 text-center text-gray-400 text-sm">Wird geladen…</div>}
      </div>

      {/* Share Modal */}
      {shareModal && (
        <Modal onClose={() => setShareModal(null)}>
          <div className="w-[450px]">
            <h2 className="text-[17px] font-bold mb-1">Share-Link</h2>
            <p className="text-[13px] text-gray-500 mb-4">Report teilen — Link kopieren oder Passwort setzen</p>

            <div className="bg-bg rounded-lg p-3 mb-4">
              <div className="text-[11px] text-gray-500 mb-1">Share-URL</div>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={shareUrl(shareModal)}
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-[12px] font-mono bg-white outline-none"
                />
                <button
                  onClick={() => { navigator.clipboard.writeText(shareUrl(shareModal)); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                  className="px-3 py-2 bg-teal text-white rounded-lg text-[12px] font-medium"
                >
                  {copied ? 'Kopiert!' : 'Kopieren'}
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Passwort setzen (optional)</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Mindestens 4 Zeichen"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal"
                />
                <button
                  onClick={handleSetPassword}
                  disabled={password.length < 4}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-[12px] hover:bg-gray-50 disabled:opacity-50"
                >
                  Setzen
                </button>
              </div>
            </div>

            {shareModal.share_password && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-[12px] text-amber-800">
                Passwort-Schutz ist aktiv. Empfänger müssen ein Passwort eingeben.
              </div>
            )}

            <div className="flex justify-end">
              <button onClick={() => setShareModal(null)} className="px-4 py-2 border border-gray-200 rounded-lg text-[13px] hover:bg-gray-50">
                Schließen
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
